// src/components/FloatingActionButtons.jsx
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

function IconWhatsApp({ className = "w-6 h-6" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M.057 24l1.687-6.163a11.8 11.8 0 0 1-1.62-6.003C.122 5.281 5.403 0 12.06 0c3.179 0 6.169 1.24 8.413 3.488a11.79 11.79 0 0 1 3.48 8.407c-.002 6.657-5.283 11.94-11.94 11.94-2.03 0-4.024-.508-5.806-1.47L.057 24Zm6.597-3.807c1.64.975 3.504 1.491 5.406 1.491 5.624 0 10.2-4.576 10.202-10.2.002-2.73-1.064-5.295-3-7.23A10.16 10.16 0 0 0 12.06 1.99C6.436 1.99 1.86 6.566 1.86 12.19c0 1.91.523 3.777 1.515 5.412l-.963 3.52 3.742-.929h.001Zm11.387-6.563c-.074-.122-.27-.196-.564-.343-.294-.147-1.74-.858-2.01-.956-.27-.099-.467-.147-.665.148-.196.294-.763.955-.935 1.15-.171.196-.343.221-.637.074-.294-.148-1.24-.457-2.36-1.457-.872-.777-1.46-1.737-1.634-2.03-.171-.294-.018-.452.13-.6.134-.133.294-.343.441-.515.148-.171.197-.294.295-.49.099-.196.05-.368-.025-.515-.074-.147-.665-1.603-.913-2.2-.24-.577-.485-.5-.665-.51l-.566-.01c-.196 0-.515.074-.786.368-.27.294-1.03 1.005-1.03 2.45 0 1.444 1.055 2.84 1.201 3.036.148.196 2.078 3.172 5.037 4.444.704.303 1.255.484 1.685.62.707.225 1.352.193 1.862.117.568-.084 1.74-.71 1.986-1.395.245-.686.245-1.273.171-1.395Z" />
    </svg>
  );
}

function IconChat({ className = "w-6 h-6" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

export default function FloatingActionButtons() {
  const { pathname } = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);

  // Ne pas afficher sur certaines pages
  const hideOnPages = ["/messagerie", "/agent-com/messagerie", "/connexion"];
  if (hideOnPages.some((page) => pathname.includes(page))) {
    return null;
  }

  // Numéro WhatsApp (à configurer)
  const whatsappNumber = "+2290159756543";
  const whatsappMessage = encodeURIComponent("Bonjour, j'aimerais avoir plus d'informations.");
  const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/\D/g, "")}?text=${whatsappMessage}`;

  // Numéro SMS (même numéro que WhatsApp)
  const smsNumber = "+22959756543";
  const smsMessage = encodeURIComponent("Bonjour, j'aimerais avoir plus d'informations.");
  const smsUrl = `sms:${smsNumber}?body=${smsMessage}`;

  const handleWhatsApp = () => {
    window.open(whatsappUrl, "_blank");
  };

  const handleChat = () => {
    // Ouvrir l'application SMS par défaut avec le numéro pré-rempli
    window.location.href = smsUrl;
  };

  return (
    <div className="fixed right-6 z-50 flex flex-col items-end gap-3 fab-position">
      <AnimatePresence>
        {isExpanded && (
          <>
            {/* Bouton Chat */}
            <motion.button
              initial={{ opacity: 0, scale: 0, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0, y: 20 }}
              transition={{ delay: 0.1 }}
              onClick={handleChat}
              className="w-14 h-14 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 flex items-center justify-center transition-colors"
              aria-label="Envoyer un SMS"
              title="Envoyer un SMS"
            >
              <IconChat className="w-6 h-6" />
            </motion.button>

            {/* Bouton WhatsApp */}
            <motion.button
              initial={{ opacity: 0, scale: 0, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0, y: 20 }}
              transition={{ delay: 0.05 }}
              onClick={handleWhatsApp}
              className="w-14 h-14 rounded-full bg-[#25D366] text-white shadow-lg hover:bg-[#128C7E] flex items-center justify-center transition-colors"
              aria-label="Contacter par WhatsApp"
              title="Contacter par WhatsApp"
            >
              <IconWhatsApp className="w-6 h-6" />
            </motion.button>
          </>
        )}
      </AnimatePresence>

      {/* Bouton principal (toggle) */}
      <motion.button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-16 h-16 rounded-full shadow-xl flex items-center justify-center transition-all ${
          isExpanded
            ? "bg-gray-600 hover:bg-gray-700"
            : "bg-blue-600 hover:bg-blue-700"
        } text-white`}
        aria-label={isExpanded ? "Fermer" : "Ouvrir le menu"}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <motion.svg
          viewBox="0 0 24 24"
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          animate={{ rotate: isExpanded ? 45 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <path d="M12 5v14M5 12h14" strokeLinecap="round" />
        </motion.svg>
      </motion.button>
    </div>
  );
}

